// src/components/personal/sections/OrdersSection/OrdersSection.tsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderData } from './hooks/useOrderData';
import { useOrdersInfo } from './hooks/useOrdersInfo';
import { TEXT_STYLES } from '@/theme';
import { OrderFilters, Order, OrderStatus, Payment } from './types/types';
import { ResizeHint } from '@/components/ui/Table/ResizeHint';
import { RefreshCw } from 'lucide-react'; // Используем иконки из lucide-react

// UI компоненты
import Toolbar from './components/Toolbar';
import Table from './components/Table';
import OrderInfo from './components/OrderInfo/OrderInfo';
import { Container, Flex, Card } from '@/components/ui/Layout';
import Message from '@/components/ui/Message';
import Pagination from '@/components/ui/Table/Pagination'; // Добавляем компонент пагинации
import { useColumnWidths } from '@/hooks/useColumnWidths';
import { displayErrorNotification } from './error/error-handlers';

interface OrdersSectionProps {
  initialOrders?: Order[];
}

const OrdersSection: React.FC<OrdersSectionProps> = ({ 
  initialOrders = []
}) => {
  const { t } = useTranslation(['orders', 'common', 'errors']);
  const [] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useColumnWidths('orders-table');
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Используем хук для работы с данными списка заказов
  const {
    filteredOrders,
    displayedOrders,
    
    // Состояние загрузки
    isLoading,
    error: ordersError,
    
    // Методы загрузки данных
    refreshOrders,
    exportOrdersToCSV,
    
    // Фильтрация
    filters,
    searchQuery,
    showFilters,
    updateFilters,
    setSearchQuery,
    toggleFilters,
    isFiltering,
    resetFilters,
    
    // Пагинация
    pagination,
    setCurrentPage,
    setPageSize,
    
    // Сортировка
    sortColumn,
    sortDirection,
    setSorting,
    
    // Общее количество заказов из API
    totalOrdersCount
  } = useOrderData({ initialOrders });

  // Используем хук для детальной информации о заказе
  const {
    orderInfo: selectedOrder,
    setOrderId: selectOrder,
    updateOrderStatus,
    addPayment,
    isLoading: isOrderInfoLoading,
    error: orderInfoError
  } = useOrdersInfo();

  // Обработчики событий UI
  const handleOrderSelect = useCallback((orderId: string) => {
    selectOrder(orderId);
  }, [selectOrder]);

  const handleOrderClose = useCallback(() => {
    selectOrder(null);
  }, [selectOrder]);

  // Обработчик обновления статуса заказа
  const handleStatusUpdate = useCallback(async (orderId: string, status: OrderStatus) => {
    try {
      await updateOrderStatus(orderId, status);
      // Обновляем список заказов после обновления статуса
      await refreshOrders(false);
    } catch (error: any) {
      console.error('Error updating order status:', error);
      displayErrorNotification({
        message: t('errors.status_update_error'),
        code: 'UNKNOWN_ERROR'
      });
    }
  }, [updateOrderStatus, refreshOrders, t]);

  // Обработчик добавления платежа
  const handleAddPayment = useCallback(async (orderId: string, paymentData: Partial<Payment>) => {
    try {
      await addPayment(orderId, paymentData);
      // Обновляем список заказов после добавления платежа
      await refreshOrders(false);
    } catch (error: any) {
      console.error('Error adding payment:', error);
      displayErrorNotification({
        message: t('errors.payment_add_error'),
        code: 'UNKNOWN_ERROR'
      });
    } 
  }, [addPayment, refreshOrders, t]);

  // Обработчик изменения фильтров
  const handleFiltersChange = useCallback((newFilters: OrderFilters) => {
    if (isLoading) {
      console.log('Загрузка в процессе, изменение фильтров отложено');
      return;
    }
    
    // Определяем, активны ли фильтры
    const hasActiveFilters = Object.values(newFilters).some(val => 
      val !== '' && val !== 'all' && val !== undefined && val !== null
    );
    
    // Обновляем фильтры
    updateFilters(newFilters);
    
    // Для отладки
    if (DEBUG) {
      console.log('Применяются фильтры:', {
        фильтры: newFilters,
        активныФильтры: hasActiveFilters
      });
    }
    
    // После обновления фильтров запускаем загрузку данных
    console.log('Запуск обновления данных после изменения фильтров');
    refreshOrders(true);
  }, [updateFilters, refreshOrders, isLoading, DEBUG]);

  // Обработчик изменения строки поиска
  const handleSearchChange = useCallback((query: string) => {
    console.log('Получен запрос на поиск:', query);
    
    if (isLoading) {
      console.log('Загрузка в процессе, запрос поиска отложен');
      return;
    }
    
    // Проверка на пустую строку, чтобы не блокировать очистку поиска
    const isEmptyQuery = query.trim() === '';
    
    // Устанавливаем поисковый запрос
    setSearchQuery(query);
    
    // После изменения поискового запроса запускаем загрузку данных
    if (!isEmptyQuery || searchQuery) {
      console.log('Запуск обновления данных после изменения поискового запроса');
      refreshOrders(true);
    }
  }, [setSearchQuery, refreshOrders, isLoading, searchQuery]);

  // Обработчик экспорта
  const handleExport = useCallback(async () => {
    await exportOrdersToCSV(filteredOrders);
  }, [exportOrdersToCSV, filteredOrders]);

  // Улучшенный обработчик изменения страницы
  const handlePageChange = useCallback((page: number) => {
    console.log(`Изменение страницы на ${page}`);
    
    // Устанавливаем новую страницу
    setCurrentPage(page);
    
    // Обновляем данные после смены страницы
    refreshOrders(false);
    
    // Дополнительная диагностика
    console.log(`Запрошена страница ${page}`);
  }, [setCurrentPage, refreshOrders]);
  
  // Улучшенный обработчик изменения размера страницы
  const handlePageSizeChange = useCallback((size: number) => {
    console.log(`Изменение размера страницы на ${size}`);
    
    // Устанавливаем новый размер страницы
    setPageSize(size);
    
    // Сбрасываем текущую страницу на первую при изменении размера
    setCurrentPage(1);
    
    // Обновляем данные после изменения размера страницы
    refreshOrders(false);
  }, [setPageSize, setCurrentPage, refreshOrders]);
  
  // Обработчик сортировки
  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSorting(column, direction);
  }, [setSorting]);

  // Обработчик изменения ширины столбца
  const handleColumnWidthsChange = useCallback((widths: Record<string, number>) => {
    setColumnWidths(widths);
  }, [setColumnWidths]);

  // Сброс ширины колонок
  const resetColumnWidths = useCallback(() => {
    setColumnWidths({});
  }, [setColumnWidths]);

  // Обработчик обновления данных
  const handleRefresh = useCallback(() => {
    if (isLoading) {
      console.log('Уже идет загрузка, обновление отменено');
      return;
    }
    
    // Всегда обновляем с принудительной загрузкой всех данных при наличии фильтров
    refreshOrders(true);
  }, [refreshOrders, isLoading]);

  // Обработчик для сброса всех фильтров
  const handleResetFilters = useCallback(() => {
    if (isLoading) {
      console.log('Уже идет загрузка, сброс фильтров отложен');
      return;
    }
    
    // Сбрасываем фильтры
    resetFilters();
    
    // После сброса фильтров обновляем данные
    refreshOrders(true);
  }, [resetFilters, refreshOrders, isLoading]);

  // Определение общего количества элементов для таблицы
  // Если фильтрация активна, используем количество отфильтрованных элементов
  // Иначе используем общее количество из API
  const totalTableItems = isFiltering ? filteredOrders.length : totalOrdersCount;
  
  // Вычисляем общее количество страниц
  const calculatedTotalPages = Math.max(1, Math.ceil(totalTableItems / pagination.pageSize));
  
  // Добавлены дополнительные отладочные логи
  if (DEBUG) {
    console.log('OrdersSection - Параметры пагинации:', {
      displayedCount: displayedOrders.length,
      filteredCount: filteredOrders.length,
      totalCount: totalOrdersCount,
      totalTableItems,
      isFiltering,
      currentPage: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalPages: calculatedTotalPages,
      isLoading
    });
  }

  // Параметры состояния UI
  const showGlobalLoader = isLoading || isOrderInfoLoading;
  const hasError = Boolean(ordersError || orderInfoError);
  const canInteract = !showGlobalLoader && !hasError;
  const errorMessage = orderInfoError || ordersError || t('errors:data_load_error');

  return (
    <Container>
      <Flex direction="column" gap="lg">
        {/* Индикатор загрузки */}
        {showGlobalLoader && (
          <Card padding="md" shadow="sm">
            <Flex align="center" justify="center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
              <span className={TEXT_STYLES.body}>
                {isFiltering && isLoading 
                  ? t('loading_all_orders', { defaultValue: 'Загрузка всех заказов для фильтрации...' }) 
                  : t('common:loading')}
              </span>
            </Flex>
          </Card>
        )}

        {/* Сообщение об ошибке */}
        {hasError && (
          <Message 
            type="error" 
            message={errorMessage}
          />
        )}

        {/* Панель инструментов с фильтрами и поиском */}
        <Toolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          showFilters={showFilters}
          onToggleFilters={toggleFilters}
          onExport={handleExport}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRefresh={handleRefresh}
          onResetFilters={handleResetFilters}
          disabled={!canInteract}
          isFiltering={isFiltering}
          totalItems={totalTableItems}
          isLoading={isLoading}
        />
        
        {/* Таблица заказов */}
        <Card shadow="sm" padding="none" className="w-full">
          <ResizeHint tableId="orders-table" />
          
          {/* Кнопка сброса колонок, если есть измененные ширины */}
          {Object.keys(columnWidths).length > 0 && (
            <div className="px-4 py-2 flex justify-end">
              <button 
                onClick={resetColumnWidths}
                className="text-xs text-gray-500 hover:text-primary transition-colors flex items-center"
                title={t('common:reset_to_default')}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {t('common:reset_columns')}
              </button>
            </div>
          )}
          
          {/* УЛУЧШЕНО: Изменены параметры передачи для таблицы */}
          <Table
            filteredOrders={displayedOrders}
            onOrderSelect={handleOrderSelect}
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnWidths={columnWidths}
            onColumnWidthsChange={handleColumnWidthsChange}
            scrollable={false}
            autoHeight={true}
            totalItems={totalTableItems}
            totalPages={calculatedTotalPages}
          />
          
          {/* УЛУЧШЕНО: Улучшен компонент пагинации */}
          {totalTableItems > 0 && (
            <div className="mt-4 px-4 py-2">
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={calculatedTotalPages}
                totalItems={totalTableItems}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSize={pagination.pageSize}
                itemName={t('table.orders_count_name', { defaultValue: 'заказов' })}
                disabled={false} // Убрана блокировка во время загрузки для улучшения UX
              />
            </div>
          )}
        </Card>

        {/* Информация о заказе (если выбран) */}
        {selectedOrder && (
          <OrderInfo
            order={selectedOrder}
            onClose={handleOrderClose}
            onStatusUpdate={handleStatusUpdate}
            onAddPayment={handleAddPayment}
            disabled={!canInteract}
          />
        )}
      </Flex>
    </Container>
  );
};

export default OrdersSection;